import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Check, CircleX, Info, TriangleAlert } from 'lucide-react'
import { cn } from '@claritio/frontend/lib/utils'

// Tone variants are signals: no background, the icon carries the tone (see
// Signal). default, secondary and outline stay as neutral labels.
const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center whitespace-nowrap text-caption font-medium',
  {
    variants: {
      variant: {
        default: 'justify-center gap-1 rounded-full border border-transparent bg-primary px-2.5 py-0.5 text-primary-foreground',
        secondary: 'justify-center gap-1 rounded-full border border-transparent bg-mist px-2.5 py-0.5 text-foreground',
        outline: 'justify-center gap-1 rounded-full border px-2.5 py-0.5 text-foreground',
        violet: 'justify-center gap-1 rounded-full border border-transparent bg-mist px-2.5 py-0.5 text-foreground',
        muted: 'gap-1.5 text-muted-foreground',
        success: 'gap-1.5 text-foreground [&>svg:first-child]:text-success',
        info: 'gap-1.5 text-foreground [&>svg:first-child]:text-brand',
        warning: 'gap-1.5 text-foreground [&>svg:first-child]:text-warning-icon',
        destructive: 'gap-1.5 text-danger',
      },
    },
    defaultVariants: { variant: 'default' },
  },
)

const TONE_ICON = { success: Check, info: Info, warning: TriangleAlert, destructive: CircleX } as const

function Badge({
  className, variant, asChild = false, children, ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span'
  const Icon = variant && variant in TONE_ICON ? TONE_ICON[variant as keyof typeof TONE_ICON] : null
  const tone = Icon || variant === 'muted'
  return (
    <Comp className={cn(badgeVariants({ variant }), tone && '[&>svg]:size-3.5 [&>svg~svg]:hidden', className)} {...props}>
      {Icon && <Icon aria-hidden />}
      {variant === 'muted' && <span aria-hidden className="size-[7px] shrink-0 rounded-full border-[1.5px] border-muted-foreground" />}
      {children}
    </Comp>
  )
}

export { Badge, badgeVariants }
